import m from "mithril";
import { MithrilTsxComponent } from "mithril-tsx-component";
import { EventAggregator } from "oj-eventaggregator";
import { delegate, pause, singleton } from "oj-promise-utils";
import { cleanString, throttle } from "oj-utils";
import { DEFAULT_IMAGE, getFullProduct, getProductImage, getProductName, nrg_url } from "../../state/api";
import { getCrosssell } from "../../state/data";
import { questions } from "../../state/data/questions";
import state from "../../state/state";
import { getText, getTextFixed } from "../../state/translations";
import { getBoundingClientRectFrom } from "../../utils/dom";
import { isLoggedIn, openLoginModal } from "../../utils/login";
import { formatCurrency, getAsset } from "../../utils/utils";
import { Button } from "../button/button";
import { dialog } from "../dialog/dialog";
import { QuantitySelector } from "../quantity-selector/quantity-selector";
import { Svg } from "../svg/svg";
import { Table } from "../table/table";
import { Tabs } from "../tabs/tabs";
import "./productDetail.scss";
import { onderlegSet } from "../../state/data/onderleg_platen";
import { Checkbox } from "../checkbox/checkbox";
let appEl;
export const sharedProductUtils = {
    ea: new EventAggregator(),
    tooltips: new Map(),
    dialogs: new Map(),
    openTooltip: singleton(async (e, product, className, onclick, fallback = false, tooltipBottom = false) => {
        for (const [k, t] of sharedProductUtils.tooltips)
            t.close();
        const sourceProduct = product;
        const variant = state.getVariant(product.category);
        if (variant)
            product = { ...product, ...variant };
        let fullProduct;
        getFullProduct(product)
            .then(info => {
            if (info)
                fullProduct = info;
            m.redraw();
        })
            .catch(err => console.error(err));
        const tooltip = dialog({
            className: "product-detail-tooltip",
            dismiss: false,
            backdrop: false,
            onopen: () => { sharedProductUtils.ea.emit("tooltip:open", sourceProduct); },
            onclose: () => { sharedProductUtils.ea.emit("tooltip:close", sourceProduct); },
            close: false,
            closeIcon: true,
            morph: () => {
                const width = 248;
                const height = 70;
                const el = document.querySelector(className);
                appEl ??= document.querySelector(".oj-App");
                const appRect = appEl?.getBoundingClientRect();
                const rect = el
                    ? getBoundingClientRectFrom(el)
                    : ((e && "clientX" in e)
                        ? { x: e.clientX - (appRect?.x ?? 0), y: e.clientY - (appRect?.y ?? 0), width, height }
                        : { x: (window.innerWidth / 2), y: ((appEl?.clientHeight ?? window.innerHeight) / 2), width, height });
                return [
                    {
                        x: rect.x + rect.width / 2 - width / 2,
                        y: rect.y + rect.height / 2 - height / 2,
                        width,
                        height
                    },
                    {
                        x: rect.x + rect.width / 2 - width / 2,
                        y: tooltipBottom
                            ? rect.y + rect.height / 2 + 40
                            : rect.y + rect.height / 2 - height - 40,
                        width,
                        height
                    }
                ];
            }
        }, _close => {
            const thumbnail = fullProduct?.thumbnail;
            return m("div", { onclick: (e) => {
                    onclick?.(fullProduct ?? product, e);
                    _close();
                } }, fallback
                ? m("span", { className: "fs5" }, getProductName(product, fullProduct))
                : [
                    m("img", { src: thumbnail ?? DEFAULT_IMAGE, className: cleanString(!thumbnail && "is-fallback-image") }),
                    m("span", { className: "fs5" }, getProductName(product, fullProduct))
                ]);
        });
        const p = pause(200);
        p.promise
            .then(() => {
            window.on("pointermove.ProductTooltip", throttle(200, ev => {
                let root = ev.target.closest(".oj-AppDialog");
                root ??= ev.target.closest(className);
                if (!root)
                    tooltip?.close();
            }));
        })
            .catch(() => { });
        const t = {
            event: e,
            close: async (e) => {
                p.reject();
                await tooltip.close(e);
            },
            promise: tooltip.promise,
            product
        };
        sharedProductUtils.tooltips.set(sourceProduct.artCodeTSNL, t);
        await tooltip.promise;
        window.off("pointermove.ProductTooltip");
        p.reject();
        sharedProductUtils.tooltips.delete(sourceProduct.artCodeTSNL);
        m.redraw();
    }),
    closeDialogs: async (e) => {
        const promises = [];
        for (const [k, d] of sharedProductUtils.dialogs)
            promises.push(sharedProductUtils.closeDialog(k, e));
        return await Promise.all(promises);
    },
    closeDialog: async (k, e) => {
        const d = sharedProductUtils.dialogs.get(k);
        if (!d)
            return;
        const p = d.promise;
        await d.close(e);
        return await p;
    },
    openDialog: singleton(async (e, product, sourceElement, parentCode) => {
        const sourceProduct = product;
        if (product.category === "nrglassen") {
            const result = await dialog({
                title: getTextFixed("NRG_LAS_CONFIGURATOR_TITLE"),
                className: "confirm-dialog",
                dismiss: false,
                closeIcon: true,
            }, _close => [
                m("p", null, getTextFixed("NRG_LAS_CONFIGURATOR_MESSAGE")),
                m("div", { className: "dialog-footer" },
                    m(Button, { text: getTextFixed("NO_CANCEL"), className: "button-ghost-default", onclick: () => _close() }),
                    m(Button, { text: getTextFixed("YES_CONTINUE"), className: "button-secondary", onclick: () => _close("confirm") }))
            ]).promise;
            if (result !== "confirm")
                return;
            window.open(nrg_url, "_blank", "noopener,noreferrer");
            return;
        }
        for (const [k, t] of sharedProductUtils.tooltips)
            t.close();
        const variant = state.getVariant(product.category);
        if (variant)
            product = { ...product, ...variant };
        let fullProduct;
        getFullProduct(product)
            .then(info => {
            fullProduct = info;
            m.redraw();
        });
        appEl ??= document.querySelector(".oj-App");
        const appHeight = appEl?.clientHeight ?? window.innerHeight;
        const margin = 20;
        const _d = dialog({
            title: () => getProductName(product, fullProduct),
            className: "product-detail-dialog",
            dismiss: true,
            backdrop: false,
            closeIcon: true,
            onbeforeopen: () => { sharedProductUtils.ea.emit("dialog:open:before", sourceProduct); },
            onopen: () => { sharedProductUtils.ea.emit("dialog:open", sourceProduct); },
            onbeforeclose: () => {
                sharedProductUtils.ea.emit("dialog:close:before", sourceProduct);
                if (window.innerWidth > 500)
                    return pause(200).promise;
            },
            onclose: () => { sharedProductUtils.ea.emit("dialog:close", sourceProduct); },
            morph: (open) => {
                const tt = Array.from(sharedProductUtils.tooltips)[0];
                if (window.innerWidth < 768) {
                    const width = window.innerWidth;
                    return [
                        {
                            x: 0,
                            y: 0,
                            width,
                            height: 0,
                        },
                        { x: 0, y: 0, width, height: appHeight },
                    ];
                }
                else {
                    const width = Math.min(window.innerWidth, 520);
                    const height = appHeight - margin * 2;
                    const el = sourceElement ?? (tt
                        ? document.querySelector(".product-detail-tooltip > div")
                        : document.querySelector(`.product-${sourceProduct.category}`));
                    const rect = el
                        ? getBoundingClientRectFrom(el)
                        : ((e && "clientX" in e)
                            ? { x: e.clientX, y: e.clientY, width: 100, height: 100 }
                            : { x: (window.innerWidth / 2), y: (height / 2), width, height });
                    return [
                        (open || sourceElement)
                            ? {
                                x: rect.x,
                                y: rect.y,
                                width: rect.width,
                                height: rect.height
                            }
                            : {
                                x: window.innerWidth,
                                y: margin,
                                width,
                                height
                            },
                        {
                            x: window.innerWidth - width - 20,
                            y: margin,
                            width,
                            height
                        },
                    ];
                }
            }
        }, _close => {
            const p = fullProduct
                ?? {
                    ...product,
                    name: getProductName(product),
                    description: product.description ?? "",
                    image: getProductImage(product).image,
                    thumbnail: getProductImage(product).thumbnail,
                    artCodeTSNL: product.artCodeTSNL ?? "UNKNOWN",
                    category: product.category,
                };
            return m(ProductDetail, { product: p, parentCode: parentCode, onconfirm: () => _d.close("confirm"), onchange: (p) => {
                    if (!p)
                        return;
                    product = p;
                    getFullProduct(product)
                        .then(info => {
                        fullProduct = info;
                        m.redraw();
                    });
                } });
        });
        const d = {
            event: e,
            close: async (e) => {
                await _d.close(e);
            },
            promise: _d.promise,
            product
        };
        sharedProductUtils.dialogs.set(sourceProduct.artCodeTSNL, d);
        await d.promise
            ?.catch(err => {
            console.error(err);
            // do nothing
        });
        sharedProductUtils.dialogs.delete(sourceProduct.artCodeTSNL);
        m.redraw();
    })
};
const incompleteAnswers = (product) => {
    if (!(product.category in questions))
        return false;
    const _answers = state.getAnswers(product.category);
    const _questions = questions[product.category];
    const _products = (state.products.filtered[product.category] ?? []);
    for (let i = 0; i < _questions.length; i++) {
        const q = _questions[i];
        const validOptionCount = q.options.filter(opt => _products.some(p => p[q.key] === opt.value)).length;
        if (validOptionCount <= 1)
            continue; // auto-answered, no user input required
        if (!_answers || _answers[i] === undefined)
            return true;
    }
    return false;
};
const incompleteInfoDialog = async () => {
    await dialog({
        title: getTextFixed("INCOMPLETE_INFO_TITLE"),
        className: "incomplete-info-dialog",
        dismiss: true,
        closeIcon: true,
    }, _close => m("div", { className: "incomplete-info" },
        m("p", null, getTextFixed("ANSWER_ALL_QUESTIONS")))).promise;
    await pause(200).promise;
    document.querySelector(".oj-AppDialog .options:not(:has(input:checked)) input").focus({ preventScroll: true });
};
const checkOptionExists = (questionKey, option, subset) => {
    let exists = false;
    outer: for (const product of subset) {
        if (("removeBefore" in product && product.removeBefore) || ("removeAfter" in product && product.removeAfter))
            continue;
        const base = "data" in product ? product.data : product;
        if (base[questionKey] === option) {
            exists = true;
            break outer;
        }
    }
    return exists;
};
class _ProductQuestions extends MithrilTsxComponent {
    results;
    oninit(v) {
        this.setQuestions(v);
    }
    view(v) {
        const prorail = state.getProrailChoice();
        return [
            m("div", { className: "ProductQuestions" }, v.attrs.questions.map(q => {
                // q.options.reduce((s, x) => s + +(!x.disabled), 0) > 1 &&
                const prorailAnswer = (prorail && q.key in prorail)
                    ? prorail[q.key]
                        ? getTextFixed("PRORAIL_ANSWER", { term: getText(prorail[q.key]) })
                        : getTextFixed("PRORAIL_ANSWER_UNKNOWN")
                    : undefined;
                return m("div", { className: "question-group" },
                    m("h2", { className: "fs4" }, getText(q.question)),
                    prorailAnswer && m("span", null, prorailAnswer),
                    m("div", { className: "options" }, q.options.map(option => {
                        if (option.hidden)
                            return;
                        return m("label", { className: cleanString(option.disabled && "-disabled") },
                            m("input", { type: "radio", disabled: option.disabled, name: q.question, value: option.value, checked: option.active, onchange: e => {
                                    for (const o of q.options)
                                        o.active = o.value === option.value;
                                    this.results = this.handleChange(v, q);
                                    v.attrs.onchange(Object.fromEntries(v.attrs.questions.map((q, i) => [i, q.options.find(o => o.active)?.index])), this.results);
                                } }),
                            m("span", null, getText(option.value)));
                    })));
            })),
            // this.results?.length > 0 ?
            //     <div className="matching-products">
            //         <div className="products">
            //             {this.results.map(p =>
            //                 <div className="product">
            //                     <span className="fs5">{p.artCodeTSNL} - {p.teknrComp} - {getProductName(p)}</span>
            //                 </div>
            //             )}
            //         </div>
            //     </div>
            //     : v.attrs.answers.length > 0 && <div className="no-results">
            //         <span className="fs5">Geen producten gevonden</span>
            //     </div>
        ];
    }
    onbeforeupdate(v, o) {
        if (v.attrs.product.artCodeTSNL !== o.attrs.product.artCodeTSNL || v.attrs.product.key !== o.attrs.product.key) {
            this.setQuestions(v);
        }
    }
    setQuestions(v) {
        for (const question of v.attrs.questions)
            for (const option of question.options) {
                const exists = checkOptionExists(question.key, option.value, v.attrs.products);
                option.hidden = !exists;
                option.disabled = !exists;
            }
        if (v.attrs.answers) {
            // let last: IProductQuestions["questions"][number]
            for (const qi in v.attrs.answers) {
                const ai = v.attrs.answers[qi];
                if (ai === undefined)
                    continue;
                const question = v.attrs.questions[Number(qi)];
                if (!question) {
                    alert(`Ongeldige vraag "${qi}" in opgeslagen antwoorden. De antwoorden zijn gereset.`);
                    break;
                }
                const option = question.options[ai];
                if (!option) {
                    alert(`Ongeldige optie "${ai}" voor vraag "${qi}" in opgeslagen antwoorden. De antwoorden zijn gereset.`);
                    break;
                }
                option.active = true;
                // last = question
            }
            this.results = this.handleChange(v, v.attrs.questions[0]);
        }
    }
    handleChange(v, current) {
        const results = v.attrs.products.map(x => ({ data: x, removeBefore: false, removeAfter: false }));
        let afterCurrent = false;
        for (const question of v.attrs.questions) {
            if (!afterCurrent) {
                const active = question.options.find(o => o.active);
                if (active)
                    for (const product of results)
                        if (product.data[question.key] !== active.value)
                            product.removeBefore = true;
            }
            if (afterCurrent) {
                for (const option of question.options) {
                    if (!checkOptionExists(question.key, option.value, results)) {
                        option.disabled = true;
                        option.active = false;
                    }
                    else
                        delete option.disabled;
                }
                const active = question.options.find(o => o.active);
                if (active)
                    for (const product of results)
                        if (product.data[question.key] !== active.value)
                            product.removeAfter = true;
            }
            const options = question.options.filter(o => !o.disabled);
            if (options.length === 1)
                options[0].active = true;
            if (question === current)
                afterCurrent = true;
        }
        for (const question of v.attrs.questions) {
            const active = question.options.find(o => o.active);
            if (active)
                continue;
            const options = question.options.filter(o => !o.disabled);
            if (options.length === 1)
                options[0].active = true;
        }
        return results.filter(r => !r.removeBefore && !r.removeAfter).map(r => r.data);
    }
}
export class ProductDetail extends MithrilTsxComponent {
    tab = "TAB_PRODUCT";
    product;
    promise;
    _quantity;
    tabs = [];
    oninit(v) {
        this.setTabs(v);
    }
    view(v) {
        const product = this.product ?? v.attrs.product;
        const { parentCode } = v.attrs;
        const currentQuantity = parentCode
            ? state.getCrosssellQuantity(parentCode, product.artCodeTSNL)
            : state.getQuantity(product.artCodeTSNL);
        const quantity = this._quantity ?? currentQuantity;
        const totalPrice = (product.price ?? 0) * (quantity || 1);
        return [
            m(Tabs, { tabs: this.tabs, onchange: async (tab) => {
                    if (tab === "TAB_PRODUCT" && this.tab === "TAB_QUESTIONS" && incompleteAnswers(product ?? v.attrs.product)) {
                        incompleteInfoDialog();
                        return;
                    }
                    this.tab = tab;
                    m.redraw();
                }, tab: this.tab }, isLoggedIn && m("div", { style: "display: none;", className: "logged-in" })),
            this.tab === "TAB_QUESTIONS"
                ? m("div", { className: "footer" },
                    m("div", { className: "footer-left" },
                        m("button", { className: "button button-default", onclick: async () => {
                                if (this.promise)
                                    await this.promise;
                                if (incompleteAnswers(product ?? v.attrs.product))
                                    incompleteInfoDialog();
                                else
                                    this.tab = "TAB_PRODUCT";
                            } },
                            m("span", { className: "fs5" }, getTextFixed("NEXT")))))
                : m("div", { className: "footer" },
                    isLoggedIn
                        ? m("span", { className: "fs3 total-price" },
                            formatCurrency(totalPrice),
                            " ",
                            m("span", { className: "tax" }, getTextFixed("EXC_VAT")))
                        : m(Button, { className: "button-grey", onclick: openLoginModal },
                            m("span", { className: "fs5" }, getTextFixed("LOGIN_MESSAGE"))),
                    m("div", { className: "footer-left" },
                        m(QuantitySelector, { min: 0, value: quantity, onchange: (value) => {
                                this._quantity = value;
                            } }),
                        m("button", { className: "button button-default", onclick: async () => {
                                // const prev = state.getQuantity(product.artCodeTSNL)
                                if (parentCode) {
                                    const q = currentQuantity > 0 ? (this._quantity === 0 ? 0 : (this._quantity ?? currentQuantity)) : (this._quantity || 1);
                                    let d = delegate();
                                    const sub = state.on("quantity:change", async (x) => {
                                        if (x.rejected && onderlegSet.has(x.artCodeTSNL)) {
                                            let acknowledged = false;
                                            const r = await dialog({
                                                className: "confirm-dialog onderlegset-warning-dialog",
                                                title: getTextFixed("ONDERLEGSET_WARNING_TITLE"),
                                                dismiss: false,
                                                close: false,
                                            }, c => [
                                                m("p", null, getTextFixed("ONDERLEGSET_WARNING_BODY_1")),
                                                m("p", null, getTextFixed("ONDERLEGSET_WARNING_BODY_2")),
                                                m("hr", { className: "warning-divider" }),
                                                m("label", { className: "warning-checkbox-label" },
                                                    m(Checkbox, { checked: acknowledged, onchange: () => { acknowledged = !acknowledged; m.redraw(); } }),
                                                    m("span", null, getTextFixed("ONDERLEGSET_WARNING_CHECKBOX"))),
                                                m("div", { className: "dialog-footer" },
                                                    m(Button, { text: getTextFixed("YES_DELETE"), className: "button-default", disabled: !acknowledged, onclick: () => acknowledged && c("remove") }),
                                                    m(Button, { text: getTextFixed("KEEP_PRODUCT"), className: "button-secondary", onclick: () => c() }))
                                            ]).promise;
                                            if (r === "remove")
                                                state.setCrosssellQuantity(parentCode, product.artCodeTSNL, q, undefined, true);
                                            else
                                                this._quantity = x.quantity;
                                        }
                                        d.resolve();
                                    });
                                    state.setCrosssellQuantity(parentCode, product.artCodeTSNL, q);
                                    sub.off();
                                    await d.promise;
                                    v.attrs.onconfirm?.();
                                }
                                else {
                                    state.setQuantity(product.artCodeTSNL, this._quantity === 0 ? 0 : (this._quantity || 1));
                                    const addedProduct = this.product ?? product;
                                    if (this._quantity !== 0) {
                                        await dialog({
                                            className: "product-added-dialog",
                                            closeIcon: true,
                                        }, close => m("div", { className: "product-added-content" },
                                            m("div", { className: "product-added-header" },
                                                m(Svg, { className: "icon check-icon", src: getAsset("icons/check-1.svg") }),
                                                m("strong", null, getTextFixed("PRODUCT_ADDED"))),
                                            m("div", { className: "product-added-card" },
                                                m("img", { src: addedProduct.thumbnail ?? DEFAULT_IMAGE, alt: getProductName(addedProduct), className: cleanString("product-added-thumb", !addedProduct.thumbnail && "is-fallback-image") }),
                                                m("div", { className: "product-added-info" },
                                                    m("span", { className: "product-added-name" }, getProductName(addedProduct)),
                                                    m("span", { className: "product-added-code" }, addedProduct.artCodeTSNL))),
                                            m("button", { className: "button button-default product-added-cta", onclick: () => {
                                                    close();
                                                    for (const [, d] of sharedProductUtils.dialogs)
                                                        d.close();
                                                    state.emit("navigate:finalize", {});
                                                } },
                                                m("span", null, getTextFixed("COMPLETE_YOUR_CONFIGURATION")),
                                                m(Svg, { className: "icon", src: getAsset("icons/arrow-right-white.svg") })),
                                            m("button", { className: "button button-default outline product-added-continue", onclick: () => {
                                                    close();
                                                    for (const [, d] of sharedProductUtils.dialogs)
                                                        d.close();
                                                } },
                                                m("span", null, getTextFixed("CONTINUE_CONFIGURING"))))).promise;
                                        // v.attrs.onconfirm?.()
                                    }
                                }
                            } },
                            m("span", { className: "fs5" }, currentQuantity > 0
                                ? this._quantity === 0
                                    ? getTextFixed("REMOVE")
                                    : getTextFixed("UPDATE")
                                : getTextFixed("ADD")))),
                    isLoggedIn && m("div", { className: cleanString("stock-status", !product.stock?.in_stock && "out-of-stock") },
                        m(Svg, { className: "icon", src: product.stock?.in_stock ? getAsset("icons/check-1.svg") : getAsset("icons/x-circle.svg") }),
                        m("span", { className: "fs5" }, product.stock?.in_stock_message || (product.stock?.in_stock ? getTextFixed("IN_STOCK") : getTextFixed("OUT_OF_STOCK")))))
        ];
    }
    onbeforeupdate(v, o) {
        if (v.attrs.product.artCodeTSNL !== o.attrs.product.artCodeTSNL) {
            // delete this.product
            this.setTabs(v);
        }
    }
    async setTabs(v) {
        this.tabs = [
            { key: "TAB_PRODUCT", label: getTextFixed("TAB_PRODUCT"), content: () => m(BasicProductInfo, { product: this.product ?? v.attrs.product }) },
        ];
        const questionTab = v.attrs.product.category in questions ? this.getQuestionsTab(v) : undefined;
        const variant = state.getVariant(v.attrs.product.category);
        if (!this.product) {
            if ("price" in v.attrs.product)
                this.product = v.attrs.product;
            else {
                this.promise = getFullProduct(variant ?? v.attrs.product);
                this.product = await this.promise;
                delete this.promise;
            }
            if (variant) {
                if ("onderlegSet" in variant && variant.onderlegSet)
                    this.product.onderlegSet = variant.onderlegSet;
                if ("crosssell" in variant && variant.crosssell?.length)
                    this.product.crosssell = variant.crosssell;
            }
        }
        this.tabs = [
            questionTab,
            { key: "TAB_PRODUCT", label: getTextFixed("TAB_PRODUCT"), content: () => m(BasicProductInfo, { product: this.product ?? v.attrs.product }) },
            this.product?.properties?.length > 0 ? { key: "TAB_SPECIFICATIONS", label: getTextFixed("TAB_SPECIFICATIONS"), content: () => this.product && m(Specifications, { product: this.product }) } : null,
            this.product?.documents?.length > 0 ? { key: "TAB_DOCUMENTS", label: getTextFixed("TAB_DOCUMENTS"), content: () => this.product && m(Documents, { product: this.product }) } : null,
        ].filter(Boolean);
        if (questionTab)
            this.tab = "TAB_QUESTIONS";
        else
            this.tab = "TAB_PRODUCT";
        // this.quantity = state.getQuantity(this.product.artCodeTSNL) ?? this.product.aantal
        m.redraw();
    }
    getQuestionsTab(v) {
        const _questions = structuredClone(questions[v.attrs.product.category]);
        const _products = state.products.filtered[v.attrs.product.category];
        const _answers = state.getAnswers(v.attrs.product.category) ?? {};
        const _onchange = async (a, r) => {
            state.setAnswers(v.attrs.product.category, a);
            if (Object.values(a).every(ai => ai !== undefined) && r?.[0]) {
                delete this.product;
                this.promise = getFullProduct(r[0]);
                this.product = await this.promise;
                delete this.promise;
                const variant = state.getVariant(v.attrs.product.category);
                if (variant) {
                    if ("onderlegSet" in variant && variant.onderlegSet)
                        this.product.onderlegSet = variant.onderlegSet;
                    if ("crosssell" in variant && variant.crosssell?.length)
                        this.product.crosssell = variant.crosssell;
                }
            }
            else {
                delete this.product;
                delete this.promise;
                delete state.products.variants[v.attrs.product.category];
            }
            v.attrs.onchange(this.product);
            m.redraw();
        };
        let has = false;
        for (const question of _questions) {
            let count = 0;
            for (const option of question.options) {
                const exists = checkOptionExists(question.key, option.value, _products);
                if (exists)
                    count++;
            }
            if (count > 1) {
                has = true;
                break;
            }
        }
        if (!has)
            return;
        const c = state.getProrailChoice();
        if (c) {
            let changed = false;
            for (let i = 0; i < _questions.length; i++) {
                if (_answers[i] !== undefined)
                    continue;
                const prorailAnswer = c?.[_questions[i].key];
                if (!prorailAnswer)
                    continue;
                const idx = _questions[i].options
                    .find(o => (o.value === prorailAnswer || o.alt === prorailAnswer) && _products.some(x => x[_questions[i].key] === o.value))?.index;
                if (idx === undefined)
                    continue;
                _answers[i] = idx;
                changed = true;
            }
            if (changed) {
                _onchange(_answers, undefined);
            }
        }
        return {
            key: "TAB_QUESTIONS",
            label: getTextFixed("QUESTIONS_LABEL"),
            content: () => m(_ProductQuestions, { product: v.attrs.product, questions: _questions, products: _products, answers: state.getAnswers(v.attrs.product.category) ?? {}, onchange: _onchange })
        };
    }
}
class BasicProductInfo extends MithrilTsxComponent {
    crosssell = [];
    oninit(v) {
        this.crosssell = getCrosssell(v.attrs.product, () => m.redraw());
    }
    onbeforeupdate(v, o) {
        if (v.attrs.product.artCodeTSNL !== o.attrs.product.artCodeTSNL
            || v.attrs.product.key !== o.attrs.product.key
            || v.attrs.product.onderlegSet !== o.attrs.product.onderlegSet)
            this.crosssell = getCrosssell(v.attrs.product, () => m.redraw());
    }
    view(v) {
        const { product } = v.attrs;
        const name = getProductName(product);
        const txt = getText(product.description, undefined, product.description);
        return m("div", { className: "Product" },
            m("div", { className: "product-main" },
                m("img", { src: product?.thumbnail ?? DEFAULT_IMAGE, alt: name, className: cleanString("product-image", !product?.thumbnail && "is-fallback-image") }),
                m("div", { className: "product-info" },
                    m("a", { href: product.link, target: "_blank", rel: "noopener noreferrer" },
                        m("h2", { className: "fs4" }, name)),
                    m("p", { className: "fs5 product-code" },
                        getTextFixed("ARTICLE_NUMBER"),
                        ": ",
                        product.artCodeTSNL))),
            m("div", { className: "divider" }),
            m("p", { className: "fs5 product-description" }, txt ? m.trust(txt) : null),
            this.crosssell.length > 0 &&
                [
                    m("div", { className: "divider" }),
                    m("div", { className: "crosssell" },
                        m("h3", { className: "fs5" }, getTextFixed("RELATED_PARTS")),
                        m("div", { className: "crosssell-list" }, this.crosssell.map((item) => {
                            const name = getProductName(item);
                            return m("button", { className: "item", onclick: async (e) => {
                                    const el = e.currentTarget.closest(".item");
                                    const product = await item.promise;
                                    sharedProductUtils.openDialog(e, product, el);
                                    m.redraw();
                                } },
                                m("div", { className: "image" },
                                    m("img", { src: item?.thumbnail ?? DEFAULT_IMAGE, alt: name, className: cleanString(!item?.thumbnail && "is-fallback-image") })),
                                m("div", { className: "title" },
                                    m("span", { className: "fs6 item-code" }, getProductName(item))));
                        })))
                ]);
    }
}
class Specifications extends MithrilTsxComponent {
    view(v) {
        const { product } = v.attrs;
        const specs = product.properties;
        return specs?.length > 0
            ? m(Table, { className: "Specifications", rows: specs, headers: [
                    {
                        key: "name",
                        label: getTextFixed("CHARACTERISTIC"),
                        size: "max(150px, 33%)",
                        className: () => "name"
                    },
                    {
                        key: "value",
                        label: getTextFixed("SPECIFICATION"),
                        size: "1fr",
                        className: () => "value",
                        view: (row) => getText(row.value, undefined, row.value)
                    }
                ] })
            : m("span", { className: "fs5" }, getTextFixed("NO_SPECIFICATIONS_AVAILABLE"));
    }
}
class Documents extends MithrilTsxComponent {
    view(v) {
        return m("div", { className: "Documents" }, v.attrs.product.documents?.map((doc) => (m("a", { className: "doc-item", href: doc.url, target: "_blank", rel: "noopener noreferrer" },
            m(Svg, { className: "icon", src: getAsset("icons/pdf.svg") }),
            m("span", { className: "fs5" }, doc.title)))));
    }
}
